import type { ReactNode } from "react";
import { AlertCircle, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";

interface ErrorCalloutProps {
  message: string;
  status?: number | null;
  title?: string;
  className?: string;
  children?: ReactNode;
}

export function ErrorCallout({
  message,
  status,
  title,
  className,
  children,
}: ErrorCalloutProps) {
  const isForbidden = status === 403;
  const Icon = isForbidden ? ShieldAlert : AlertCircle;
  const resolvedTitle =
    title || (isForbidden ? "Access denied" : "Something went wrong");

  return (
    <div
      role="alert"
      className={cn(
        "rounded-2xl border px-4 py-3 sm:px-5 sm:py-4",
        isForbidden
          ? "border-amber-200 bg-gradient-to-b from-amber-50 to-orange-50 text-amber-900"
          : "border-red-200 bg-gradient-to-b from-red-50 to-rose-50 text-red-900",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full",
            isForbidden ? "bg-amber-100" : "bg-red-100",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-5">{resolvedTitle}</p>
          <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap break-words">
            {message}
          </p>
          {children ? <div className="mt-2">{children}</div> : null}
        </div>
      </div>
    </div>
  );
}

